import React, { useRef } from "react";

import { cn } from "../../../common/u1/cn";
import { getShorthandName } from "../../../../utils/Helper";
import { ImageIcon, TrashIcon } from "../../../../assets/ui/icons";
import { ACCEPT_IMAGES } from "../../../common/u1/uploadTypes";

/* The profile photo, and the two things you can do to it. */
export const ProfileAvatar = ({
  userInfo,
  busy = false,
  canRemove = false,
  onSelect,
  onRemove,
  className = "",
}) => {
  const inputRef = useRef(null);
  const open = () => inputRef.current?.click();
  const initials = getShorthandName(userInfo?.name || "");

  return (
    <div className={cn("flex flex-col items-center gap-3", className)}>
      <button
        type="button"
        onClick={open}
        aria-label={userInfo?.profile_image ? "Change your photo" : "Add a photo"}
        className={cn(
          "relative flex h-[140px] w-[140px] items-center justify-center overflow-hidden",
          "rounded-full border-4 border-white bg-[var(--field-inset)]",
          "shadow-[0_2px_10px_rgba(1,34,52,.12)] transition-colors",
          "hover:border-[var(--coral-soft)] focus-visible:outline-none",
          "focus-visible:ring-2 focus-visible:ring-[var(--coral)] focus-visible:ring-offset-2"
        )}
      >
        {busy ? (
          <span
            role="status"
            aria-live="polite"
            className="text-[12.5px] font-semibold text-[var(--muted)] font-sans"
          >
            Uploading…
          </span>
        ) : userInfo?.profile_image ? (
          <img
            src={userInfo.profile_image}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <span
            className={cn(
              "font-bold text-[var(--navy)] font-sans",
              initials.length > 2 ? "text-[34px]" : "text-[44px]"
            )}
          >
            {initials}
          </span>
        )}
      </button>

      <input
        ref={inputRef}
        id="file-input"
        type="file"
        aria-label="Choose a profile photo"
        accept={ACCEPT_IMAGES}
        className="hidden"
        onChange={(e) => {
          onSelect?.(e.target.files?.[0]);
          /* So choosing the same file twice still fires a change. */
          e.target.value = "";
        }}
      />

      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={open}
          disabled={busy}
          className={cn(
            "inline-flex items-center gap-1.5 rounded text-[13px] font-semibold",
            "text-[var(--coral)] font-sans transition-colors",
            "hover:text-[var(--coral-deep)] focus-visible:outline-none",
            "focus-visible:ring-2 focus-visible:ring-[var(--coral)]",
            "disabled:cursor-not-allowed disabled:opacity-50"
          )}
        >
          <ImageIcon size={13} />
          Edit
        </button>

        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            className={cn(
              "inline-flex items-center gap-1.5 rounded text-[13px] font-semibold",
              "text-[var(--muted)] font-sans transition-colors",
              "hover:text-[var(--coral-deep)] focus-visible:outline-none",
              "focus-visible:ring-2 focus-visible:ring-[var(--coral)]",
              "disabled:cursor-not-allowed disabled:opacity-50"
            )}
          >
            <TrashIcon size={13} />
            Remove
          </button>
        )}
      </div>
    </div>
  );
};

export default ProfileAvatar;
